import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Typography, Box, Container } from '@material-ui/core'
import NoteAddIcon from 'components/material-symbol/icons/NoteAdd'
import SectionTitle from 'components/section-title'
import ApolloDataView from 'components/apollo-data-view'
import TipWell from 'components/tip-well'
import LoadingView from '../../loading-view'
import { useSnackbar } from 'notistack'
import { useSnackbarSuccess } from 'lib/snackbar'
import {
  useGetNotes,
  useCreateNote,
  useUpdateNote,
  useDeleteNote,
} from './api-hooks'
import NoteCard from './note-card'
import NoteComposer from './note-composer'
import DeleteNoteDialog from './delete-note-dialog'
import { sortNotesByUpdatedAt } from './session-note-utils'
import { useStudentNotesStyles } from './styles'

const TIP_COPY =
  'Keep track of important information about your student, their sessions, and their progress. Choose whether to keep the note private or share it with the student.'

function StudentNotes({ studentId, studentFirstName }) {
  const classes = useStudentNotesStyles()
  const { enqueueSnackbar } = useSnackbar()
  const snackbarSuccess = useSnackbarSuccess(enqueueSnackbar)

  const { data, loading, error, refetch } = useGetNotes(studentId)
  const [createNote, { loading: creating }] = useCreateNote(refetch)
  const [updateNote, { loading: updating }] = useUpdateNote(refetch)
  const [deleteNote, { loading: deleting }] = useDeleteNote(refetch)
  const [editingNote, setEditingNote] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)

  const notes = data?.notes ?? []
  const sortedNotes = sortNotesByUpdatedAt(notes)

  const handleCreate = useCallback(
    async payload => {
      try {
        await createNote({
          notableId: studentId,
          notableType: 'Student',
          title: payload.title,
          content: payload.content,
          private: payload.isPrivate,
        })
        snackbarSuccess('Note created.')
      } catch (err) {
        enqueueSnackbar(err?.message || 'Failed to save note.', {
          variant: 'error',
        })
        throw err
      }
    },
    [createNote, studentId, snackbarSuccess, enqueueSnackbar],
  )

  const handleUpdate = useCallback(
    async payload => {
      if (!editingNote) return
      try {
        await updateNote({
          id: editingNote.id,
          title: payload.title,
          content: payload.content,
          private: payload.isPrivate,
        })
        setEditingNote(null)
        snackbarSuccess('Note updated.')
      } catch (err) {
        enqueueSnackbar(err?.message || 'Failed to save note.', {
          variant: 'error',
        })
      }
    },
    [editingNote, updateNote, snackbarSuccess, enqueueSnackbar],
  )

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return
    try {
      await deleteNote(deleteTarget.id)
      if (editingNote?.id === deleteTarget.id) {
        setEditingNote(null)
      }
      setDeleteTarget(null)
      snackbarSuccess('Note deleted.')
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to delete note.', {
        variant: 'error',
      })
    }
  }, [deleteTarget, deleteNote, editingNote, snackbarSuccess, enqueueSnackbar])

  return (
    <Container className={classes.notesContainer}>
      <ApolloDataView error={error} loading={loading} data={data}>
        {{
          errorView: (
            <Typography color="error">
              Failed to load notes. Please try again.
            </Typography>
          ),
          loadingView: <LoadingView />,
          emptyView: null,
          dataView: () => (
            <Box className={classes.notesColumn}>
              <div className={classes.header}>
                <TipWell icon={NoteAddIcon} caption={TIP_COPY} />
              </div>
              {!editingNote ? (
                <NoteComposer
                  mode="create"
                  isEditing={false}
                  studentFirstName={studentFirstName}
                  onSubmit={handleCreate}
                  isSubmitting={creating}
                />
              ) : null}
              {sortedNotes.length > 0 ? (
                <Box className={classes.notesList}>
                  <SectionTitle size="card" className={classes.notesListTitle}>
                    Notes
                  </SectionTitle>
                  {sortedNotes.map(note =>
                    editingNote?.id === note.id ? (
                      <NoteComposer
                        key={note.id}
                        mode="edit"
                        isEditing
                        note={note}
                        studentFirstName={studentFirstName}
                        onSubmit={handleUpdate}
                        onCancel={() => setEditingNote(null)}
                        isSubmitting={updating}
                      />
                    ) : (
                      <NoteCard
                        key={note.id}
                        note={note}
                        onEdit={() => setEditingNote(note)}
                        onDelete={() => setDeleteTarget(note)}
                        onClick={() => setEditingNote(note)}
                      />
                    ),
                  )}
                </Box>
              ) : null}
            </Box>
          ),
        }}
      </ApolloDataView>

      <DeleteNoteDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        disabled={deleting}
      />
    </Container>
  )
}

StudentNotes.propTypes = {
  studentId: PropTypes.string.isRequired,
  studentFirstName: PropTypes.string,
}

StudentNotes.defaultProps = {
  studentFirstName: null,
}

export default StudentNotes
